import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useCanvasEditor } from '@/components/canvas/CanvasEditorContext';
import {
  buildSaveFields,
  getChangedFields,
  serializeSaveFields,
  type CanvasSaveFields,
  type SerializedSaveFields,
} from '@/components/canvas/CanvasDocument';
import { createCanvasPreviewFile } from '@/components/canvas/canvasPreview';
import { canvasService, CanvasServiceError, type UpdateCanvasData } from '@/services/canvasService';

// Saves 3s after the editing stops, and at the latest 30s after the first unsaved change so continuous
// editing still gets saved.
export const AUTOSAVE_DEBOUNCE_MS = 3000;
export const AUTOSAVE_MAX_WAIT_MS = 30000;
// Waits between retries of a failed save (the last one repeats).
export const AUTOSAVE_RETRY_DELAYS_MS = [5000, 15000, 30000, 60000];

export type AutosaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

// Saves now; resolves true once the backend has every change (and the current image, with withImage).
export type FlushSave = (options?: { withImage?: boolean }) => Promise<boolean>;

interface SaveOptions {
  // Also upload the preview image if the canvas changed since the last upload.
  withImage?: boolean;
  // Preview already rendered (taken while closing, before the canvas is disposed).
  imageFile?: File | null;
}

interface CurrentDocument {
  fields: CanvasSaveFields;
  serialized: SerializedSaveFields;
}

// Network errors, server errors, timeouts and rate limits may work later; other 4xx won't by retrying.
const isRetryable = (error: unknown) => {
  const status = error instanceof CanvasServiceError ? error.status : undefined;
  return status === undefined || status >= 500 || status === 408 || status === 429;
};

const fingerprint = (serialized: SerializedSaveFields) => JSON.stringify(serialized);

const pickFields = (fields: CanvasSaveFields, keys: (keyof CanvasSaveFields)[]): UpdateCanvasData =>
  Object.fromEntries(keys.map((key) => [key, fields[key]])) as UpdateCanvasData;

const parseSavedAt = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  // A server clock ahead of ours would read "in a few seconds".
  return date.getTime() > Date.now() ? new Date() : date;
};

interface UseCanvasAutosaveOptions {
  canvasId: string;
  // modified_at of the loaded canvas.
  initialSavedAt?: string | null;
}

// Keeps the canvas saved with as few requests as possible:
// - The source is the history entry being shown, which only changes when the user changes something
//   (and on undo/redo). Nothing is polled.
// - Saves are debounced, only send the fields that changed, and are skipped when the document matches
//   the saved one (e.g. undoing back to it).
// - One request at a time: changes made meanwhile go in a single follow-up save.
// - Autosaves are JSON only; the preview image is uploaded on manual save and when leaving the editor,
//   and only if the canvas changed since the last upload.
export const useCanvasAutosave = ({ canvasId, initialSavedAt }: UseCanvasAutosaveOptions) => {
  const {
    canvas,
    documentReady,
    historyEntries,
    historyIndex,
  } = useCanvasEditor();
  const snapshot = historyEntries[historyIndex]?.snapshot;

  const current = useMemo<CurrentDocument | null>(() => {
    if (!documentReady || !snapshot) return null;
    const fields = buildSaveFields(snapshot);
    return { fields, serialized: serializeSaveFields(fields) };
  }, [documentReady, snapshot]);

  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(() => parseSavedAt(initialSavedAt));
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);

  // Timers and listeners read the latest values through refs, so they never need re-registering.
  const currentRef = useRef(current);
  currentRef.current = current;
  const canvasRef = useRef(canvas);
  canvasRef.current = canvas;
  const hasSavedRef = useRef(lastSavedAt !== null);

  // Last fields the backend has, and the document the stored image was rendered from.
  const savedRef = useRef<SerializedSaveFields | null>(null);
  const imageFingerprintRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  // Settles (never rejects) when the running request ends; flush() waits on it.
  const inFlightPromiseRef = useRef<Promise<void>>(Promise.resolve());
  const queuedRef = useRef<SaveOptions | null>(null);
  const unmountedRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxWaitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);

  const clearTimer = (timerRef: { current: ReturnType<typeof setTimeout> | null }) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const isDirty = () => {
    const latest = currentRef.current;
    return !!latest && !!savedRef.current && getChangedFields(latest.serialized, savedRef.current).length > 0;
  };

  const isImageOutdated = () => {
    const latest = currentRef.current;
    return !!latest && imageFingerprintRef.current !== fingerprint(latest.serialized);
  };

  const saveRef = useRef<(options?: SaveOptions) => Promise<void>>(async () => {});

  const schedule = useCallback(() => {
    // A pending retry already carries the latest changes; saving earlier would skip the backoff.
    if (retryTimerRef.current) return;
    clearTimer(debounceTimerRef);
    debounceTimerRef.current = setTimeout(() => void saveRef.current(), AUTOSAVE_DEBOUNCE_MS);
    maxWaitTimerRef.current ??= setTimeout(() => void saveRef.current(), AUTOSAVE_MAX_WAIT_MS);
  }, []);

  saveRef.current = async ({ withImage = false, imageFile }: SaveOptions = {}) => {
    const latest = currentRef.current;
    const saved = savedRef.current;
    if (!latest || !saved) return;

    if (inFlightRef.current) {
      const queued = queuedRef.current;
      queuedRef.current = {
        withImage: withImage || !!queued?.withImage,
        imageFile: imageFile ?? queued?.imageFile,
      };
      return;
    }

    clearTimer(debounceTimerRef);
    clearTimer(maxWaitTimerRef);
    clearTimer(retryTimerRef);
    // Offline: the 'online' listener saves when the connection is back.
    if (!navigator.onLine) return;

    const changed = getChangedFields(latest.serialized, saved);
    const image =
      imageFile ??
      (withImage && isImageOutdated()
        ? createCanvasPreviewFile(canvasRef.current, latest.fields.width, latest.fields.height)
        : null);
    if (changed.length === 0 && !image) return;

    const payload = pickFields(latest.fields, changed);
    if (image) payload.image = image;

    inFlightRef.current = true;
    let failed = false;
    setStatus('saving');
    try {
      const request = canvasService.updateCanvas(canvasId, payload);
      inFlightPromiseRef.current = request.then(
        () => undefined,
        () => undefined
      );
      await request;
      savedRef.current = { ...savedRef.current, ...Object.fromEntries(changed.map((key) => [key, latest.serialized[key]])) } as SerializedSaveFields;
      if (image) imageFingerprintRef.current = fingerprint(latest.serialized);
      retryAttemptRef.current = 0;
      hasSavedRef.current = true;
      setLastSavedAt(new Date());
      setStatus(isDirty() ? 'dirty' : 'saved');
    } catch (error) {
      failed = true;
      setStatus('error');
      if (isRetryable(error) && !unmountedRef.current) {
        const delay = AUTOSAVE_RETRY_DELAYS_MS[Math.min(retryAttemptRef.current, AUTOSAVE_RETRY_DELAYS_MS.length - 1)];
        retryAttemptRef.current += 1;
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          void saveRef.current({ withImage, imageFile: image });
        }, delay);
      }
    } finally {
      inFlightRef.current = false;
      const queued = queuedRef.current;
      queuedRef.current = null;
      if (queued) void saveRef.current(queued);
      // After a failure only a retry, a new change or a manual save tries again (no request loop).
      else if (!failed && isDirty() && !unmountedRef.current) schedule();
    }
  };

  // Reacts to every change of the document.
  useEffect(() => {
    if (!current) return;
    // First document after loading: it is what the backend already has (image included).
    if (!savedRef.current) {
      savedRef.current = current.serialized;
      imageFingerprintRef.current = fingerprint(current.serialized);
      return;
    }
    if (getChangedFields(current.serialized, savedRef.current).length === 0) {
      // Back to the saved state (e.g. undo): nothing to save.
      clearTimer(debounceTimerRef);
      clearTimer(maxWaitTimerRef);
      setStatus((prev) => (prev === 'dirty' ? (hasSavedRef.current ? 'saved' : 'idle') : prev));
      return;
    }
    setStatus((prev) => (prev === 'error' || prev === 'saving' ? prev : 'dirty'));
    schedule();
  }, [current, schedule]);

  const saveNow = useCallback(() => {
    retryAttemptRef.current = 0;
    void saveRef.current({ withImage: true });
  }, []);

  // Saves now and resolves once the backend has every change (true) or a save failed/couldn't run (false).
  // For actions that read the canvas back from the backend, like the JSON and HTML exports. withImage also
  // uploads the image when it's outdated (the HTML links to it).
  const flush = useCallback<FlushSave>(async ({ withImage = false } = {}) => {
    const isSaved = () => !isDirty() && !(withImage && isImageOutdated());
    // A running save (and the follow-up it may start right after) goes first.
    while (inFlightRef.current) await inFlightPromiseRef.current;
    if (!isSaved()) {
      retryAttemptRef.current = 0;
      await saveRef.current({ withImage });
      while (inFlightRef.current) await inFlightPromiseRef.current;
    }
    return isSaved();
    // isDirty only reads refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (isDirty()) void saveRef.current();
    };
    const handleOffline = () => setIsOnline(false);
    // The tab may never come back (closed from the OS, mobile...): save while it still runs.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isDirty()) void saveRef.current();
    };
    // Closing/reloading the tab: a keepalive request outlives the page. If it can't be used (payload too
    // big), the browser asks before leaving.
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const latest = currentRef.current;
      if (!latest || !savedRef.current || !isDirty()) return;
      const payload = pickFields(latest.fields, getChangedFields(latest.serialized, savedRef.current));
      if (!canvasService.updateCanvasOnUnload(canvasId, payload)) event.preventDefault();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        saveNow();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleKeyDown);
    };
    // isDirty only reads refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasId, saveNow]);

  // Leaving the editor: saves what's pending plus the preview if it's outdated. A layout effect because its
  // cleanup runs before the stage's (passive) cleanup disposes the fabric canvas the preview is taken from.
  useLayoutEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      clearTimer(debounceTimerRef);
      clearTimer(maxWaitTimerRef);
      clearTimer(retryTimerRef);
      const latest = currentRef.current;
      if (!latest) return;
      const imageFile = isImageOutdated()
        ? createCanvasPreviewFile(canvasRef.current, latest.fields.width, latest.fields.height)
        : null;
      void saveRef.current({ imageFile });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, lastSavedAt, isOnline, saveNow, flush };
};
